/* eslint-disable no-restricted-syntax -- 도메인 산재 색상 (1~2건/색) — 디자인 시스템 토큰화 효율 < 50%, 별도 디자인 정리 plan으로 분리 */
import { style, styleVariants, type StyleRule } from '@vanilla-extract/css';
import type { LeaveType } from '@aegislab/shared';

// 남의 예약 라벨 — 짙은 초록 히트맵 칸(#156936) 위에 놓여서 기본 텍스트 토큰으로는 AA 미달이다.
const OTHERS_NAME = '#C8D7DD';
const OTHERS_NAME_TEAM = '#B6D8FE';
import { vars } from '../../theme/contract.css';
import { mobile } from '../../theme/media';

const STANDARD_HOURS = 8;

export const cell = style({
  borderRadius: 0,
  backgroundColor: vars.surface.liquidGlass.innerSurface.backgroundL1,
  boxShadow: `inset 0 1px 0 ${vars.opacityScale.whiteOverlay.ultraFaint}`,
  // 데스크톱은 그리드 트랙(gridAutoRows:1fr)을 그대로 채워 행 간 빈틈/겹침 방지.
  // aspectRatio:1 로 정사각 고정하면 셀 높이가 트랙보다 커서 아래 행과 겹치거나(짧은 패널)
  // 트랙보다 작아 떠 보임(긴 패널). 정사각 터치 타깃이 필요한 모바일에서만 aspectRatio 유지.
  minHeight: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: vars.typography.fontFamily.mono,
  // Why: 20px 상한이 없으면 날짜 숫자가 폭(cqw) 축을 따라 무한정 커지는데 행 높이는 세로 축을
  // 따라 작아진다 — 창을 가로로 늘리면 숫자 line box(fontSize×1.205)가 셀을 넘어 overflow:hidden
  // 에 잘리고, 아래 이름(othersName)까지 밀어낸다. 이름은 이미 11px 상한이 있어 반응하지 않는
  // 비대칭이라 상한을 여기에도 맞춰 건다. 20px = 기존 데스크톱 표준 폭(grid 406px)의 실측 5cqw
  // 값이라 정상 형상은 그대로 두고 폭 확대분만 잘라낸다.
  fontSize: 'min(5cqw, 20px)',
  fontWeight: vars.typography.fontWeight.medium,
  color: vars.typography.textColor.primary,
  textShadow: `0 0 4px ${vars.opacityScale.blackOverlay.dense}`,
  position: 'relative',
  // 휴가자 이름(othersNames)이 셀 밖으로 흘러 인접 행을 침범하지 않도록 셀 안에서 클립
  overflow: 'hidden',
  userSelect: 'none',
  '@media': {
    [mobile]: {
      aspectRatio: '1',
      minHeight: '44px',
      minWidth: '44px',
    },
  },
});

// 휴가자 상세 토글용 버튼 루트 — cell 클래스와 조합, todayCell 보더가 이겨야 하므로 todayCell보다 먼저 선언
export const cellButton = style({
  appearance: 'none',
  margin: 0,
  padding: 0,
  border: 0,
  width: '100%',
  cursor: 'pointer',
  ':focus-visible': {
    outline: `2px solid ${vars.bright.primary.default}`,
    outlineOffset: -2,
  },
});

export const todayCell = style({
  border: `2px solid ${vars.bright.primary.default}`,
  boxShadow: `0 0 6px ${vars.bright.glow.primary}`,
});

export const weekendText = style({
  color: vars.typography.textColor.tertiary,
});

// 공휴일 숫자색 — 관리자 근무표의 HOLIDAY 배지와 같은 status.error.
// weekendText 뒤에 선언해 주말이면서 공휴일인 날(광복절이 토요일 등)은 이 색이 이긴다.
export const holidayText = style({
  color: vars.bright.status.error,
});

// 휴일명 — 휴가자 이름(othersName)과 같은 자리·크기. 모바일에서도 숨기지 않는다:
// 색만 남으면 색약 사용자에게 주말과의 구분이 사라진다.
export const holidayName = style({
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: 'clamp(7px, 2.4cqw, 11px)',
  lineHeight: String(1.15),
  fontWeight: vars.typography.fontWeight.medium,
  color: vars.bright.status.error,
  textShadow: `0 0 4px ${vars.opacityScale.blackOverlay.dense}`,
});

// 월급일 이름표 — 휴일명과 같은 자리. 월급일은 근무한 평일이라 초록 칸 위에 놓이는데 금색 글자는
// 그 위에서 대비가 모자라(8시간 칸 3.3), 기간 이름(spanLabel)처럼 색 바탕 + 어두운 글자로 쓴다(13.9).
// 글자도 spanLabel 과 같은 11px·줄 13px 로 고정한다 — 휴일명처럼 폭(cqw)을 따르면 모바일 그리드(308px)에서 7.4px 까지 준다.
// 숫자 색은 바꾸지 않으므로 이름표가 사라지면 월급일인지 알 수 없다 — 칸이 모자랄 때 flex 가
// overflow:hidden 인 줄부터 0 으로 누르니 줄지 않게 막고, 휴가자 이름 줄이 대신 잘리게 한다.
// 폭도 말줄임하지 않는다 — 짧은 창에서 칸이 이름표(36.5px)보다 좁아지면(1280×400 에서 36.3px) 「월…」이 된다.
// 가운데 정렬이라 넘친 만큼 양옆 금색 여백만 칸 경계(overflow:hidden)에 잘리고 글자(28.5px)는 남는다.
export const paydayName = style({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  padding: '0 4px',
  borderRadius: 3,
  backgroundColor: vars.bright.medal.gold.base,
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: 11,
  lineHeight: '13px',
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.surface.background.primary,
  textShadow: 'none',
});

export const correctionDot = style({
  position: 'absolute',
  top: 3,
  right: 3,
  width: 6,
  height: 6,
  borderRadius: '50%',
  pointerEvents: 'none',
  '@media': {
    [mobile]: {
      top: 4,
      right: 4,
      width: 7,
      height: 7,
    },
  },
});

export const correctionDotVariants = styleVariants({
  PENDING: { backgroundColor: `${vars.bright.tailwind.amber500Strong}` },
  APPROVED: { backgroundColor: vars.bright.primary.default },
});

// 데스크톱은 좌상단(정정 dot 우상단과 대칭). 모바일은 othersBadge(좌상단)와 겹치므로 좌하단으로 회피.
export const offDayDot = style({
  position: 'absolute',
  top: 3,
  left: 3,
  width: 6,
  height: 6,
  borderRadius: '50%',
  pointerEvents: 'none',
  '@media': {
    [mobile]: {
      top: 'auto',
      bottom: 4,
      left: 4,
      width: 7,
      height: 7,
    },
  },
});

export const offDayDotVariants = styleVariants({
  PENDING: { backgroundColor: vars.bright.tailwind.violet },
  APPROVED: { backgroundColor: vars.bright.tailwind.blue400 },
  REJECTED: { backgroundColor: `${vars.bright.tailwind.red500Bold}` },
});

/**
 * 개인 일정 표식 — 우하단 점 하나. 있음/없음만 말한다.
 *
 * 다른 표식과 자리를 나눠 갖는다: 정정은 우상단, 비근무일은 좌상단(모바일에서 좌하단),
 * 휴가 줄무늬는 아래 가장자리 전체. 우하단이 남은 유일한 모서리다.
 * `bottom` 을 줄무늬(3px) 위로 올려 겹치지 않게 한다.
 */
export const scheduleDot = style({
  position: 'absolute',
  bottom: 4,
  right: 3,
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: vars.bright.tailwind.amber400,
  pointerEvents: 'none',
  '@media': {
    [mobile]: {
      width: 7,
      height: 7,
      right: 4,
    },
  },
});

export const leaveStripe = style({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 3,
  pointerEvents: 'none',
  '@media': {
    [mobile]: {
      height: 4,
    },
  },
});

export const leaveStripeVariants = styleVariants({
  ANNUAL: { backgroundColor: `${vars.bright.tailwind.indigoStrong}` },
  SICK: { backgroundColor: `${vars.bright.tailwind.red500Bold}` },
  SPECIAL: { backgroundColor: 'rgba(168, 85, 247, 0.65)' },
} satisfies Record<LeaveType, StyleRule>);

// 카운트 배지 — 모바일 전용 (데스크톱은 셀 내 이름 직접 표기가 대체)
/** 팀 마감 수 — 좌상단(아래쪽은 기간 띠 자리). 숫자라 일정 점과 헷갈리지 않는다 */
export const deadlineBadge = style({
  position: 'absolute',
  top: 3,
  left: 3,
  minWidth: 14,
  height: 14,
  padding: '0 3px',
  borderRadius: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: 10,
  fontWeight: vars.typography.fontWeight.bold,
  lineHeight: String(1),
  backgroundColor: vars.bright.status.warning,
  color: vars.surface.background.primary,
  pointerEvents: 'none',
});

/**
 * 기간 일정 선 — 칸 아래쪽부터 줄마다 6px 간격으로 4px 선을 쌓는다(높이는 인라인 bottom). 이웃 칸과
 * 이어지도록 좌우를 칸 끝까지 민다. 얇아서 날짜 숫자를 덮지 않는다.
 */
export const spanLine = style({
  position: 'absolute',
  left: 0,
  right: 0,
  height: 4,
  pointerEvents: 'none',
});
/** 히트맵 초록·공휴일 빨강과 겹치지 않는 다섯 색 */
export const spanTones = [
  style({ backgroundColor: vars.bright.tailwind.blue400 }),
  style({ backgroundColor: vars.bright.tailwind.pink500 }),
  style({ backgroundColor: vars.bright.tailwind.amber400 }),
  style({ backgroundColor: vars.bright.tailwind.violet }),
  style({ backgroundColor: vars.bright.tailwind.orange400 }),
];
/**
 * 기간 이름 — 자기 선 위에 겹쳐 앉는다. 선 색 위에서도 읽히게 밝은 글자에 어두운 그림자를 두르고,
 * 그 주 안에서 기간이 이어지는 칸만큼 옆으로 뻗는다(폭은 인라인).
 */
export const spanLabel = style({
  position: 'absolute',
  left: 4,
  // 달력(isolation: isolate) 안의 모든 칸·선보다 위 — 이웃 칸의 선이 이름을 덮지 않게
  zIndex: 3,
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: 11,
  lineHeight: '13px',
  padding: '0 4px',
  borderRadius: 3,
  fontWeight: vars.typography.fontWeight.bold,
  // 바탕은 자기 선과 같은 색(spanTones), 글자는 어둡게 — 작은 글자는 밝은 바탕·어두운 글자가 더
  // 잘 읽히고(positive polarity), 색이 같아 어느 선의 이름인지 바로 이어진다. 폭은 글자에 맞춘다
  color: vars.surface.background.primary,
  textShadow: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textAlign: 'left',
  pointerEvents: 'none',
});
/** 이름이 시작하는 칸만 넘침을 푼다 — 이름이 옆 칸으로 뻗어야 해서 */
// 칸에는 zIndex 를 주지 않는다 — 칸이 쌓임 맥락을 만들면 이름이 그 칸 안에 갇혀, 이름이 뻗어 간
// 오른쪽 칸(또 다른 기간의 시작 칸)의 선이 이름을 덮는다. 이름(spanLabel)만 달력 전체에서 선 위로 올린다.
export const cellLabelOverflow = style({ overflow: 'visible' });
export const spanLineEdges = styleVariants({
  start: { left: 4, borderRadius: '2px 0 0 2px' },
  middle: {},
  end: { right: 4, borderRadius: '0 2px 2px 0' },
  single: { left: 4, right: 4, borderRadius: 2 },
});

/** 마감 2건 이상 + 휴가자 — 팀 일정에서 "사람이 비는 날" 을 칸 테두리로 띄운다 */
export const crunchCell = style({
  boxShadow: `inset 0 0 0 2px ${vars.bright.status.warning}`,
});

export const othersBadge = style({
  position: 'absolute',
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: vars.typography.fontFamily.mono,
  fontWeight: vars.typography.fontWeight.bold,
  lineHeight: String(1),
  pointerEvents: 'none',
  '@media': {
    [mobile]: {
      display: 'inline-flex',
      top: 4,
      left: 4,
      minWidth: 14,
      height: 14,
      padding: '0 3px',
      borderRadius: 8,
      fontSize: 9,
    },
  },
});

/** 휴가자 이름을 접은 칸의 숫자 줄 — 숫자와 인원 배지를 한 줄에 둔다 */
export const dayNumberRow = style({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
});

/**
 * 숫자 옆 휴가자 인원 배지 — 왼쪽 위에 두면 이름표가 숫자를 위로 올린 칸에서 배지가 숫자를 덮는다.
 * 어느 폭에서 보일지는 아래 두 수식어가 정한다(시안 3·4절).
 */
export const othersBadgeInline = style({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 14,
  height: 14,
  padding: '0 3px',
  borderRadius: 8,
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: 9,
  fontWeight: vars.typography.fontWeight.bold,
  lineHeight: String(1),
  textShadow: 'none',
});

/** 칸이 낮아 이름을 접었을 때 — 데스크톱만. 모바일은 왼쪽 위 othersBadge 가 이미 이름을 대신한다 */
export const othersBadgeInlineDesktop = style({
  display: 'inline-flex',
  '@media': {
    [mobile]: {
      display: 'none',
    },
  },
});

/** 모바일 월급일 칸 — 왼쪽 위 배지 대신 숫자 옆에. 데스크톱 수식어 뒤에 선언해 둘 다 붙으면 모바일에서 이긴다 */
export const othersBadgeInlineMobile = style({
  '@media': {
    [mobile]: {
      display: 'inline-flex',
    },
  },
});

export const othersBadgeVariants = styleVariants({
  // 우리팀 휴가자 포함 — 히트맵 그린·휴가 스트라이프와 구분되는 블루
  team: { backgroundColor: vars.bright.tailwind.blue400, color: 'rgba(2, 12, 27, 0.92)' },
  // 전사만 — 낮은 채도로 한 단계 물러남
  company: {
    backgroundColor: 'rgba(148, 163, 184, 0.45)',
    color: vars.typography.textColor.primary,
  },
});

// 이름 표기 셀 — 날짜 숫자 바로 아래에 휴가자 이름을 붙여 표기.
// (이름을 셀 바닥에 absolute 고정하면 세로로 큰 데스크톱 셀에서 숫자와 멀리 떨어져
//  아래 행 셀로 넘친 것처럼 보임 → 정상 플로우로 숫자에 밀착)
export const cellWithNames = style({
  flexDirection: 'column',
  gap: 2,
  '@media': {
    [mobile]: {
      gap: 0,
    },
  },
});

// 셀 내 휴가자 이름 직접 표기 — 데스크톱 전용 (모바일 44px 셀은 배지로 대체). 숫자 바로 아래 정상 플로우
export const othersNames = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1,
  maxWidth: '100%',
  pointerEvents: 'none',
  '@media': {
    [mobile]: {
      display: 'none',
    },
  },
});

export const othersName = style({
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: 'clamp(7px, 2.4cqw, 11px)',
  lineHeight: String(1.15),
  color: OTHERS_NAME,
  textShadow: `0 0 4px ${vars.opacityScale.blackOverlay.dense}`,
});

export const othersNameTeam = style({
  color: OTHERS_NAME_TEAM,
  fontWeight: vars.typography.fontWeight.bold,
});

export { STANDARD_HOURS };
